import { useState, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import { storage } from '../../config/firebase';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';
import { ref as dbRef, update } from 'firebase/database';
import { db } from '../../config/firebase';
import { Spinner } from '../ui/Spinner';

const ProfilePictureUpload = ({ currentPhotoURL, onPhotoUpdate, onClose }) => {
  const { currentUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Create a unique file name
      const fileName = `profile-pictures/${currentUser.uid}/${Date.now()}-${
        file.name
      }`;
      const imageRef = storageRef(storage, fileName);

      // Upload file
      await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(imageRef);

      // Update user profile in database
      await update(dbRef(db, `users/${currentUser.uid}`), {
        photoURL: downloadURL,
      });

      // Notify parent component
      onPhotoUpdate(downloadURL);
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (!currentUser) {
    return null; // Don't render if no user is logged in
  }

  return (
    <div className="relative flex flex-col items-center space-y-4">
      <button
        type="button"
        onClick={onClose}
        className="absolute right-0 top-0 inline-flex h-10 w-10 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-500 transition hover:bg-stone-50 hover:text-stone-700"
        aria-label="Close photo upload"
      >
        <FaTimes className="h-4 w-4" />
      </button>

      <div className="relative">
        <img
          src={
            currentPhotoURL ||
            `https://ui-avatars.com/api/?name=${currentUser.firstName}+${currentUser.lastName}&background=0D8ABC&color=fff`
          }
          alt="Profile"
          className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
        />
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
            <Spinner size="sm" />
          </div>
        )}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />

      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="rounded-2xl border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary-200"
        >
          {uploading ? 'Uploading...' : 'Change Photo'}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={uploading}
          className="rounded-2xl border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-600 transition hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-primary-200"
        >
          Cancel
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default ProfilePictureUpload;
